/**
 * Per-app configuration of the shared web core. Each first-party web app
 * (customer portal, staff control plane) names itself once at startup; the
 * API uses the name for cookie-mode sessions.
 */
export type WebClient = "dashboard" | "control";

let client: WebClient = "dashboard";
const responseObservers = new Set<(response: Response) => void | Promise<void>>();
const signOutObservers = new Set<() => void>();

export function configureWebApp(options: { client: WebClient }): void {
  client = options.client;
}

/** Headers marking a request as coming from a first-party web app. */
export function webHeaders(): Record<string, string> {
  return { "x-wirevod-client": client, "x-wirevod-csrf": "1" };
}

/** Observe every API response (e.g. to notice a suspended workspace). */
export function onApiResponse(observer: (response: Response) => void | Promise<void>): () => void {
  responseObservers.add(observer);
  return () => responseObservers.delete(observer);
}

/** Awaited by the API client, so observers see a response before callers do. */
export async function notifyApiResponse(response: Response): Promise<void> {
  await Promise.all(
    [...responseObservers].map(async (observer) => {
      try {
        await observer(response);
      } catch {
        // An observer must never break the request it watches.
      }
    }),
  );
}

/** Run app-specific cleanup when the user signs out. */
export function onSignOut(observer: () => void): () => void {
  signOutObservers.add(observer);
  return () => signOutObservers.delete(observer);
}

export function notifySignOut(): void {
  for (const observer of signOutObservers) observer();
}
